import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BootstrapPayload, UserIdentity } from "../core";
import { useBootstrap, useMe } from "../react/queries/useBoot";
import { useLanguages } from "../react/queries/useLanguages";
import { SessionProvider, useSession } from "../react/providers/SessionProvider";
import { WorkspaceProvider, useWorkspace } from "../react/providers/WorkspaceProvider";
import { useLocale, useT } from "../react/providers/LocaleProvider";
import { useWorkspaceColorMode } from "../theme/ThemeProvider";
import { useDocumentTitle } from "../react/hooks/useDocumentTitle";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { usePlatformCommands } from "../react/runtime/usePlatformCommands";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { buildWorkspacePath } from "../core";
import { ActionManager } from "./ActionManager";
import { CommandPalette } from "./CommandPalette";
import { ConfirmHost } from "./ConfirmDialog";
import { MainMenuManager } from "./MainMenuManager";
import { ModuleMenuManager } from "./ModuleMenuManager";
import { WorkspaceLoader } from "./WorkspaceLoader";
import { firstAppAction } from "./workspace-utils";
import { CommandPaletteProvider } from "../react/providers/CommandPaletteProvider";
import { useCommandPalette } from "../react/hooks/useCommandPalette";
import { AssistantPanelProvider } from "../react/providers/AssistantPanelProvider";
import { AssistantBindingProvider } from "../react/providers/AssistantBindingProvider";
import { DialogActionStackProvider } from "./DialogActionStack";
import { DialogActionHost } from "./DialogActionHost";
import { AssistantPanel } from "./assistant/AssistantPanel";

// The /wc/$orgCode/$ route. Runs the boot saga, mounts the workspace provider
// stack, connects SSE, registers platform commands, and renders the shell.
export function WorkspaceManager() {
    useWorkspaceColorMode(); // /wc follows the user's light/dark/system choice
    const me = useMe();
    const boot = useBootstrap();
    const t = useT();

    if (me.isLoading || boot.isLoading) {
        return <WorkspaceLoader />;
    }
    if (me.isError || boot.isError || !me.data || !boot.data) {
        return <CenteredMessage>{t("workspace.error")}</CenteredMessage>;
    }

    return (
        <SessionProvider value={me.data}>
            <WorkspaceProvider value={boot.data}>
                <CommandPaletteProvider>
                    <AssistantPanelProvider>
                        <AssistantBindingProvider>
                            <DialogActionStackProvider>
                                <PreferenceSync identity={me.data} />
                                <OrgReconciliation />
                                <DefaultActionRedirect />
                                <CommandRegistration />
                                <SseConnection />
                                <WorkspaceShell />
                            </DialogActionStackProvider>
                        </AssistantBindingProvider>
                    </AssistantPanelProvider>
                </CommandPaletteProvider>
            </WorkspaceProvider>
        </SessionProvider>
    );
}

// Global Cmd+K / Ctrl+K hotkey — toggles the workspace command palette. Lives
// inside <CommandPaletteProvider> so it can call useCommandPalette(); installed
// once at the WorkspaceShell root. Ignores keystrokes that already targeted an
// input/textarea/contentEditable element so a "K" inside a search box still
// types a literal "K"; the chord still wins because we require meta/ctrl.
function CmdKHotkey() {
    const { toggle } = useCommandPalette();

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (event.key.toLowerCase() !== "k") return;
            if (!(event.metaKey || event.ctrlKey)) return;
            // Don't steal the chord from a field that already has a real
            // Cmd+K handler (e.g. a rich-text "insert link" combobox).
            const target = event.target as HTMLElement | null;
            if (target) {
                const tag = target.tagName;
                if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
                    // Inputs accept the chord only if we own it; the meta/ctrl
                    // gate above already filters bare 'k'. Continue and toggle.
                }
            }
            event.preventDefault();
            toggle();
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [toggle]);

    return null;
}

function CenteredMessage({ children }: { children: ReactNode }) {
    return <div className="flex min-h-screen items-center justify-center font-sans">{children}</div>;
}

// Read-only apply: the active language + timezone come from the user's own
// preference. res.language is only the master we look the id up in to get the
// code + direction, so the layout switches (LTR/RTL) to match the user's choice.
function PreferenceSync({ identity }: { identity: UserIdentity }) {
    // Destructure the setters only — depending on the whole `locale` object
    // would re-fire these effects on every state change (locale memo
    // recomputes), which calls the setter again → infinite loop. The
    // setters are useCallback-stable so they're safe to use in deps.
    const { setTimezone, setLanguage } = useLocale();
    const { data: languages } = useLanguages(Boolean(identity.languageId));

    useEffect(() => {
        if (identity.timezone) setTimezone(identity.timezone);
    }, [identity.timezone, setTimezone]);

    useEffect(() => {
        if (!identity.languageId || !languages) return;
        const lang = languages.find((l) => l.id === identity.languageId);
        if (lang) setLanguage(lang.code, lang.direction);
    }, [identity.languageId, languages, setLanguage]);

    return null;
}

function CommandRegistration() {
    usePlatformCommands();
    return null;
}

// MUST-NOT-LOSE: a deep link to /wc/{orgCode}/… whose org differs from the JWT's
// active org re-mints the token to that org; an unknown org code bounces to the resolver.
function OrgReconciliation() {
    const session = useSession();
    const runtime = useRuntime();
    const { orgCode, navigateTo } = useWorkspaceNavigation();
    const queryClient = useQueryClient();
    const done = useRef(false);

    useEffect(() => {
        if (done.current) return;
        const target = session.allowedOrganizations.find((o) => o.code === orgCode);
        if (!target) {
            done.current = true;
            navigateTo("/wc");
            return;
        }
        if (target.id !== session.activeOrganizationId) {
            done.current = true;
            void runtime.services.auth.switchOrganization(target.id).then(() => queryClient.invalidateQueries());
        }
    }, [orgCode, session, runtime, navigateTo, queryClient]);

    return null;
}

// Landing on /wc/{org} with no action segment auto-loads the first app's first
// menu (apps are sequence-sorted) and reflects it in the URL.
function DefaultActionRedirect() {
    const boot = useWorkspace();
    const { orgCode, stack, navigateTo } = useWorkspaceNavigation();
    const done = useRef(false);

    useEffect(() => {
        if (done.current || stack[0].actionKey) return;
        const action = firstAppAction(boot);
        if (!action) return;
        done.current = true;
        navigateTo(
            buildWorkspacePath(orgCode, [{ kind: "action", actionKey: action.actionKey, viewMode: action.viewMode }]),
        );
    }, [boot, orgCode, stack, navigateTo]);

    return null;
}

function SseConnection() {
    const runtime = useRuntime();
    useEffect(() => {
        runtime.sse.connect("/api/web-push/events");
        return () => runtime.sse.disconnect();
    }, [runtime]);
    return null;
}

function resolveActionLabel(boot: BootstrapPayload, actionKey: string): string {
    for (const menus of Object.values(boot.menus)) {
        const found = findMenuLabel(menus, actionKey);
        if (found) return found;
    }
    return actionKey || "Home";
}

function findMenuLabel(menus: BootstrapPayload["menus"][string], actionKey: string): string | undefined {
    for (const menu of menus) {
        if (menu.actionKey === actionKey) return menu.action?.name ?? menu.name;
        if (menu.children?.length) {
            const nested = findMenuLabel(menu.children, actionKey);
            if (nested) return nested;
        }
    }
    return undefined;
}

// Phase 2 shell: top bar (MainMenuManager) + section-nav (ModuleMenuManager) +
// action area. The list / kanban / form views land in Phase 3.
function WorkspaceShell() {
    const boot = useWorkspace();
    const session = useSession();
    const { stack } = useWorkspaceNavigation();
    const action = stack[0];
    const label = resolveActionLabel(boot, action.actionKey);
    const activeOrg = session.allowedOrganizations.find((o) => o.id === session.activeOrganizationId);
    useDocumentTitle(label, activeOrg?.name);

    return (
        <div className="app-shell">
            <MainMenuManager />
            <ModuleMenuManager />
            {/* Workspace row — flex container so the assistant panel pushes
                the action content area to the left when open (push-layout,
                NOT a fixed overlay). When closed the panel returns null and
                <main> takes the full row width. */}
            <div className="workspace-row">
                <main className="workspace-content">
                    {action.actionKey ? (
                        <ActionManager />
                    ) : (
                        <div className="workspace-placeholder">
                            <p className="workspace-placeholder__note">Loading…</p>
                        </div>
                    )}
                </main>
                <AssistantPanel />
            </div>
            {/* Singleton host for the imperative confirm() API. Renders the
                actual Radix Dialog whenever a confirmation is pending. */}
            <ConfirmHost />
            {/* Action-driven dialog overlay — renders one Radix Dialog per
                entry on the DialogActionStack (any OPEN_ACTION with
                target=dialog). URL never changes; nested opens stack. */}
            <DialogActionHost />
            {/* Global Cmd+K / Ctrl+K hotkey + the palette itself. Both consume
                useCommandPalette(), so they must render inside the provider
                tree above. */}
            <CmdKHotkey />
            <CommandPalette />
        </div>
    );
}
